import { createFileRoute, Outlet, useNavigate, useParams } from '@tanstack/react-router';
import axios from 'axios';
import { useEffect, useState } from 'react';
import { z } from 'zod';
import { useCampaign, CampaignState } from '@/api';
import { PageLayout, PageLoadingState } from '@/components/layout';
import { Button, Typography } from '@/primer/components';
import { hasClaimedProduct, parseDateMs } from '@/utils';

export const Route = createFileRoute('/_authenticated/campaign/$id')({
  params: {
    parse: ({ id }) => ({ id: z.coerce.number().int().positive().parse(id) }),
    stringify: ({ id }) => ({ id: String(id) }),
  },
  component: CampaignRouteBoundary,
});

function CampaignBlocked({ title, onRetry }: { title: string; onRetry?: () => void }) {
  const navigate = useNavigate();

  return (
    <PageLayout className="py-8">
      <div className="flex flex-col items-center gap-4 text-center">
        <Typography variant="header" size="h1" color="error" as="h1">
          {title}
        </Typography>
        {onRetry && (
          <Button variant="secondary" onClick={onRetry}>
            Retry
          </Button>
        )}
        <Button onClick={() => navigate({ to: '/' })}>Go to Home</Button>
      </div>
    </PageLayout>
  );
}

export function CampaignRouteBoundary({ children }: { children?: React.ReactNode }) {
  const { id } = useParams({ from: '/_authenticated/campaign/$id' });
  const { data: campaign, isLoading, error, refetch } = useCampaign(Number(id));
  const state = campaign?.state?.toLowerCase();
  const isUnclaimedInvitation =
    state === CampaignState.Available ||
    (state === CampaignState.Participating && !hasClaimedProduct(campaign?.userProducts));
  const invitationExpiry = campaign?.invitationExpiresAt
    ? parseDateMs(campaign.invitationExpiresAt)
    : null;
  const [elapsedExpiry, setElapsedExpiry] = useState<number | null>(null);

  useEffect(() => {
    if (!isUnclaimedInvitation || invitationExpiry === null) return;

    const remaining = invitationExpiry - Date.now();
    if (remaining <= 0) return;

    const timeout = setTimeout(() => setElapsedExpiry(invitationExpiry), remaining);
    return () => clearTimeout(timeout);
  }, [isUnclaimedInvitation, invitationExpiry]);

  if (isLoading) {
    return <PageLoadingState />;
  }

  if (error) {
    if (axios.isAxiosError(error) && error.response?.status === 404) {
      return <CampaignBlocked title="Campaign Not Found" />;
    }

    return <CampaignBlocked title="Failed to load campaign" onRetry={() => refetch()} />;
  }

  if (!campaign) {
    return <CampaignBlocked title="Campaign Not Found" />;
  }

  if (
    isUnclaimedInvitation &&
    invitationExpiry !== null &&
    (elapsedExpiry === invitationExpiry || invitationExpiry <= Date.now())
  ) {
    return <CampaignBlocked title="Invitation expired" />;
  }

  return children ?? <Outlet />;
}
